import { logger } from '@zorro/common-utils';
import {
  callEndpoint,
  downloadFile,
  showErrorNotification,
} from '@zorro/web/ui-utils';
import { useState } from 'react';

/** Long enough for the browser to have started the download before we revoke. */
const OBJECT_URL_TTL_MS = 60_000;

type Params = {
  employeeId: string;
  /**
   * The MAJOR_MEDICAL benefit being enrolled. The Copilot API is keyed by
   * benefit — like the carrier-application actions (deeplink / EnrollConnect) —
   * because a carrier submission is one plan at one carrier.
   */
  benefitId: string;
};

/**
 * Downloads the enrollment as an `EnrollmentDto` JSON file — the "speak plain
 * truth" payload the Enrollment Copilot extension consumes (the extension adapts
 * it to its own fill-ready shape). Mirrors the tornado quote-dump pattern:
 * fetch-on-demand, serialize to pretty JSON, trigger a browser download.
 *
 * See `.agents/agent-ops/enrollment-copilot/`.
 */
export const useEnrollmentDumpDownload = ({
  employeeId,
  benefitId,
}: Params) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadEnrollmentDump = async () => {
    if (isDownloading) {
      return;
    }
    setIsDownloading(true);
    try {
      const enrollment = await callEndpoint({
        method: 'enrollmentCopilotControllerGetEnrollment',
        params: [employeeId, benefitId],
      });
      const json = JSON.stringify(enrollment, null, 2);
      const blob = new Blob([json], {
        type: 'application/json;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);
      downloadFile(url, `zorro_enrollment_${benefitId}.json`);
      // `downloadFile` doesn't revoke, so without this the blob — a full enrollment,
      // i.e. PHI — stays reachable for the life of the document, once per download.
      // Deferred rather than revoked in `finally`: the browser starts the download
      // asynchronously and revoking too early cancels it.
      setTimeout(() => URL.revokeObjectURL(url), OBJECT_URL_TTL_MS);
    } catch (error) {
      logger.error('Failed to download enrollment JSON dump', error);
      showErrorNotification({
        message: 'Failed to download the enrollment JSON. Please try again.',
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return { downloadEnrollmentDump, isDownloading };
};
